import { cache } from 'react';

import { RequestMetrics } from '../metrics';

// Revalidation timeout
const REVALIDATE = parseInt(process.env.REVALIDATE_TIMEOUT || '5');
const TIMEOUT = parseInt(process.env.FETCH_TIMEOUT || '30000');

/**
 * Cached fetch function that wraps the native fetch API with React's cache and Next.js auto caching.
 * Implements automatic caching with 5-minute revalidation to prevent endpoint spamming.
 *
 * @param {Request} request - The Request object containing URL, method, headers, and body
 * @param {number} [timeoutMs=30000] - Timeout in milliseconds for the request (default: 30 seconds)
 * @returns {Promise<Response>} Promise that resolves to the Response object
 * @throws {Error} Throws error for failed requests, timeouts, or network issues
 *
 * @example
 * ```typescript
 * const request = new Request('/api/data', { method: 'GET' });
 * const response = await cachedFetch(request, 10000);
 * const data = await response.json();
 * ```
 */
export const cachedFetch = cache(async (request: Request, timeoutMs: number = TIMEOUT): Promise<Response> => {
  const metrics = new RequestMetrics(request.url, request.method);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  // Check if no-cache is specified in request headers
  const shouldCache = request.cache !== 'no-cache' && request.headers?.get('Cache-Control') !== 'no-cache';

  const options = {
    signal: controller.signal,
    method: request.method,
    headers: Object.fromEntries(request.headers.entries()),
    // Only add Next.js caching if no-cache is not specified
    ...(shouldCache && { next: { revalidate: REVALIDATE } }),
  };

  metrics.setRequestOptions(options);

  try {
    const response = await fetch(request, {
      signal: controller.signal,
      // Next.js 14 auto caching configuration - only if caching is allowed
      ...(shouldCache && { next: { revalidate: REVALIDATE } }),
    });

    if (!response.ok) {
      const error = new Error(`Request failed: ${response.status} ${response.statusText} for ${request.url}`);
      metrics.logEnd(response, undefined, error);
      throw error;
    }

    // Note: We can't read response.json() here as it would consume the stream
    metrics.logEnd(response);
    return response;
  } catch (error) {
    const errorObj = error instanceof Error ? error : new Error(String(error));

    if (error instanceof DOMException && error.name === 'AbortError') {
      const timeoutError = new Error(`Request timeout after ${timeoutMs}ms for ${request.url}`);
      metrics.logEnd(undefined, undefined, timeoutError);
      throw timeoutError;
    }

    console.error(`Fetch error for ${request.url}:`, error);
    metrics.logEnd(undefined, undefined, errorObj);
    throw errorObj;
  } finally {
    clearTimeout(timeoutId);
  }
});
